/**
 * Fase 6.3 — publieke legal pages (Privacy + Voorwaarden).
 *
 * Belangrijke uitgangspunten:
 *   • Alle inhoud is uitdrukkelijk conceptversie en wordt vóór de commerciële
 *     lancering juridisch beoordeeld.
 *   • Geen certificeringen (SOC2/ISO27001/...) of andere onbevestigde claims.
 *   • Alleen gedrag dat de app vandaag daadwerkelijk implementeert — Privacy
 *     is stuk voor stuk aan de code gecheckt (zie test/PRD-matrix).
 *   • Beide talen leven naast elkaar in dezelfde file zodat vertaalgaten niet
 *     kunnen ontstaan.
 *   • Één centrale "Laatst bijgewerkt" waarde uit dict.js zodat Privacy en
 *     Terms altijd consistent zijn.
 *   • Fase 6.3: geen zichtbare <REVIEW>/TODO/FIXME/TBD-tekst op de Privacy-
 *     pagina. Onzekerheden staan als gewone volzin ("wordt vóór de
 *     commerciële lancering juridisch getoetst") in de content.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";

/* ------------------------------------------------------------------ */
/*  Fase 6.5B — canonical legal version badge. We pull the value from */
/*  /api/legal/current so /terms and /privacy always show the exact   */
/*  version that the backend gate + acceptance flow key off. Never    */
/*  a hardcoded fallback string, otherwise the two could drift.       */
/* ------------------------------------------------------------------ */
function LegalVersionBadge({ docKey }) {
  const { t } = useI18n();
  const [version, setVersion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/legal/current")
      .then((r) => { if (!cancelled) setVersion(r.data); })
      .catch(() => { /* leave null — do not fabricate a version */ });
    return () => { cancelled = true; };
  }, []);
  if (!version) return null;
  const v = docKey === "privacy" ? version.privacy_notice_version : version.terms_version;
  return (
    <span data-testid={`${docKey}-version`}>
      {t("legal_version_label")}: <code className="text-[hsl(var(--text))]">{v}</code>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Kleine review-marker (Terms behoudt deze — Privacy gebruikt hem   */
/*  niet meer, conform fase 6.3-eisen).                               */
/* ------------------------------------------------------------------ */
const REVIEW = ({ children }) => (
  <em className="text-[hsl(var(--warn))] dark:text-[hsl(38_92%_65%)] font-medium not-italic">
    [{children}]
  </em>
);

function Shell({ title, docKey, sections }) {
  const { t, lang, setLang } = useI18n();
  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Kop met terug-link en taalwissel */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="font-display text-xl font-bold hover:text-[hsl(var(--primary))] transition-colors"
          >
            {t("legal_back_home")}
          </Link>
          <button
            type="button"
            data-testid="legal-lang-toggle"
            onClick={() => setLang(lang === "nl" ? "en" : "nl")}
            className="text-[12px] font-semibold text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
            aria-label={t("legal_lang_switch")}
          >
            {lang === "nl" ? "EN" : "NL"}
          </button>
        </div>

        {/* Concept-banner */}
        <div
          data-testid="legal-draft-banner"
          className="mb-6 p-3 rounded-xl border hairline bg-[hsl(38_92%_94%)] text-[hsl(28_82%_35%)] text-[12.5px] leading-relaxed dark:bg-[hsl(38_45%_18%)] dark:text-[hsl(38_92%_82%)]"
        >
          {t("legal_draft_banner")}
        </div>

        {/* Titel + versie */}
        <h1 className="font-display text-3xl font-bold mb-1">{title}</h1>
        <div
          className="text-[12px] text-[hsl(var(--text-muted))] mb-6 flex flex-wrap items-center gap-x-3 gap-y-1"
          data-testid="legal-last-updated"
        >
          <span>{t("legal_last_updated_label")}: {t("legal_last_updated_value")}</span>
          <LegalVersionBadge docKey={docKey} />
        </div>

        {/* Inhoudsopgave */}
        <nav
          aria-label={t("legal_toc_title")}
          className="mb-8 p-4 rounded-2xl border hairline bg-[hsl(var(--surface))]"
          data-testid={`${docKey}-toc`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--text-muted))] mb-2">
            {t("legal_toc_title")}
          </div>
          <ol className="text-[13.5px] leading-relaxed grid sm:grid-cols-2 gap-x-6 gap-y-1 list-decimal pl-5">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="hover:text-[hsl(var(--primary))] transition-colors"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Secties */}
        <article
          className="prose text-[15px] leading-[1.75] text-[hsl(var(--text))] max-w-none space-y-9"
          data-testid={`${docKey}-page`}
        >
          {sections.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`${docKey}-section-${s.id}`}
              className="scroll-mt-24"
            >
              <h2 className="font-display text-[20px] font-bold mb-3">
                {i + 1}. {s.title}
              </h2>
              <div className="space-y-3 text-[hsl(var(--text))]">{s.body}</div>
            </section>
          ))}
        </article>

        {/* Onder-navigatie tussen Privacy en Terms */}
        <div className="mt-12 pt-6 border-t hairline flex items-center justify-between text-[12.5px] text-[hsl(var(--text-muted))]">
          <Link to="/privacy" className="hover:text-[hsl(var(--text))]">
            {t("legal_privacy")}
          </Link>
          <Link to="/terms" className="hover:text-[hsl(var(--text))]">
            {t("legal_terms")}
          </Link>
          <a
            href="mailto:rosterhelp@gmail.com"
            className="hover:text-[hsl(var(--text))]"
          >
            rosterhelp@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Kleine helper voor de bewaartermijnen-tabel                        */
/* ------------------------------------------------------------------ */
function RetentionTable({ rows, headers }) {
  return (
    <div className="my-2 overflow-x-auto rounded-xl border hairline">
      <table className="w-full text-[13.5px] leading-relaxed">
        <thead className="bg-[hsl(var(--surface))]">
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left font-semibold px-3 py-2 border-b hairline">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-[hsl(var(--surface))/50]" : ""}>
              <td className="align-top px-3 py-2 font-semibold whitespace-nowrap border-t hairline">{r[0]}</td>
              <td className="align-top px-3 py-2 border-t hairline">{r[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ================================================================== */
/*  PRIVACY — Nederlands                                              */
/* ================================================================== */
const PRIVACY_NL = [
  {
    id: "wat-is-rostr",
    title: "Wat is Rostr?",
    body: (
      <>
        <p>
          Rostr is een online SaaS-platform voor personeelsplanning.
          Organisaties gebruiken Rostr onder meer om:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>medewerkers te beheren (naam, contactgegevens, functie, team, locatie, rol);</li>
          <li>roosters en diensten te plannen en te publiceren;</li>
          <li>verlof en afwezigheid vast te leggen en te beoordelen;</li>
          <li>gewerkte uren in te dienen, te beoordelen en goed te keuren;</li>
          <li>dienstruilen aan te vragen en goed te keuren;</li>
          <li>meldingen (notificaties) uit te sturen bij relevante gebeurtenissen;</li>
          <li>rapportages op te bouwen uit de bovenstaande gegevens;</li>
          <li>teams en meerdere locaties gescheiden te beheren.</li>
        </ul>
        <p>
          De eigenaar van een bedrijf kan optioneel een abonnement afsluiten
          via Stripe. Rostr biedt daarnaast Google-inloggen als
          authenticatie-optie naast inloggen met e-mailadres en wachtwoord.
        </p>
      </>
    ),
  },
  {
    id: "voor-wie",
    title: "Voor wie geldt deze privacyverklaring?",
    body: (
      <>
        <p>Deze verklaring beschrijft hoe persoonsgegevens worden verwerkt bij het gebruik van Rostr. Ze geldt voor:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>eigenaren die een bedrijf op Rostr aanmaken en beheren;</li>
          <li>managers en teamleiders die binnen dat bedrijf een deel van het personeel of de planning aansturen;</li>
          <li>werknemers die via Rostr hun rooster inzien, verlof aanvragen, uren indienen of dienstwissels afhandelen;</li>
          <li>personen die per e-mail zijn uitgenodigd voor een bedrijf en het proces van registreren nog niet hebben afgerond.</li>
        </ul>
        <p>
          De verklaring dekt uitdrukkelijk niet: gegevens die je werkgever buiten
          Rostr om verwerkt, of gegevens die je op andere systemen deelt.
        </p>
      </>
    ),
  },
  {
    id: "rollen-avg",
    title: "Verwerkingsrollen: bedrijf en Rostr",
    body: (
      <>
        <p>
          Rostr is een B2B-platform. Voor een groot deel van de
          personeelsgegevens die in Rostr terechtkomen — bijvoorbeeld wie welke
          dienst draait, welk verlof is aangevraagd of welke uren zijn
          ingediend — bepaalt jouw werkgever (of het bedrijf waarvoor je
          werkt) waarom die gegevens binnen de organisatie worden vastgelegd
          en gebruikt. Rostr levert de softwaredienst waarmee die verwerking
          mogelijk wordt gemaakt.
        </p>
        <p>Voor bepaalde eigen activiteiten van Rostr kan de juridische rol anders liggen. Denk daarbij aan:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>accountbeveiliging (onder andere inlogpogingen, sessiebeheer, re-authenticatie);</li>
          <li>support-vragen die je ons rechtstreeks stuurt;</li>
          <li>facturatie richting het bedrijf via onze betaalprovider;</li>
          <li>misbruikpreventie en foutopsporing;</li>
          <li>interne technische administratie van het platform.</li>
        </ul>
        <p>
          De definitieve juridische rolverdeling — waaronder de vraag wie voor
          welk deel verwerkingsverantwoordelijke of verwerker is — wordt vóór
          de commerciële lancering formeel vastgelegd, in overleg met de
          werkgevers die Rostr gebruiken. Deze verklaring is daarom een
          conceptversie en beweert niet dat Rostr altijd verwerker is of
          altijd verwerkingsverantwoordelijke.
        </p>
      </>
    ),
  },
  {
    id: "gegevens",
    title: "Welke persoonsgegevens verwerkt Rostr?",
    body: (
      <>
        <p>De volgende categorieën zijn gebaseerd op de datamodellen die vandaag daadwerkelijk in de code aanwezig zijn.</p>
        <p><strong>A. Account- en identificatiegegevens</strong> — e-mailadres, naam, gebruikers-ID, rol (owner / manager / teamlead / employee), authenticatieprovider (wachtwoord of Google), wachtwoord-hash (uitsluitend als je met wachtwoord bent geregistreerd), refresh-tokens voor sessiebeheer en tijdstip van accountaanmaak.</p>
        <p><strong>B. Werk- en organisatiegegevens</strong> — bedrijf, locatie-ID, team-ID, functie / job title (optioneel), afdeling (optioneel), rol binnen Rostr, uren-per-week volgens contract (indien ingevuld door de werkgever), en voor managers/teamleiders de scope van locaties en medewerkers die zij mogen aansturen.</p>
        <p><strong>C. Contact- en profielgegevens</strong> — voor zover door de gebruiker of werkgever ingevuld: telefoonnummer, geboortedatum, profielfoto (avatar), noodcontact, taalvoorkeur en display-thema (light / dark / system).</p>
        <p><strong>D. Roostergegevens</strong> — per dienst worden datum, begintijd, eindtijd, pauze (indien van toepassing), locatie, team, gekoppelde medewerker (of "open shift" als er nog geen medewerker aan hangt), rol-label, notities en publicatiestatus (draft of published) vastgelegd, plus een aanmaakmoment.</p>
        <p><strong>E. Urenregistratie</strong> — per ingediende urenregel worden de shift-ID, de daadwerkelijke start- en eindtijd, een optionele notitie, de status (submitted / approved / rejected) en de manager-opmerking bij goed- of afkeuring vastgelegd.</p>
        <p><strong>F. Verlof en afwezigheid</strong> — per verlofaanvraag worden de start- en einddatum, het type (vakantie, ziek, persoonlijk, onbetaald of overig), een optionele niet-medische administratieve reden (vrije tekst, alleen voor verloftypes anders dan ziek), de status (pending / approved / rejected), de manager-opmerking, de aanvrager en de aanmaaktijd vastgelegd. Voor <em>ziek</em> slaat Rostr geen vrij tekstveld op; alleen dát iemand ziekmeldt en over welke periode. Zie ook sectie 8.</p>
        <p><strong>G. Dienstruilen</strong> — per ruilverzoek worden de aanvrager, de betrokken dienst, eventueel een specifieke doel-medewerker, een optioneel bericht, de status (pending / accepted / declined / approved / cancelled) en het tijdstip van de laatste beslissing vastgelegd.</p>
        <p><strong>H. Meldingen</strong> — per notificatie worden de ontvanger, een titel, een optionele body-tekst, het type, een eventuele link naar de app, de gelezen/ongelezen-status en het aanmaakmoment vastgelegd. Als je web-pushmeldingen hebt aangezet, bewaren we ook de push-abonnementsgegevens (endpoint plus versleutelingssleutels van de browser) die nodig zijn om jouw toestel te bereiken.</p>
        <p><strong>I. Rapportagegegevens</strong> — Rostr slaat rapportages niet apart op. Ze worden op het moment van opvragen samengesteld uit bestaande operationele gegevens (rooster, uren, verlof).</p>
        <p><strong>J. Technische en beveiligingsgegevens</strong> — beheeracties (bijvoorbeeld het aanmaken of verwijderen van een medewerker, het goedkeuren van uren, het exporteren van gegevens, het aanvragen of annuleren van een bedrijfsverwijdering) worden vastgelegd in een auditlog met tijdstip, uitvoerder, actietype en doel. Voor gevoelige acties zoals exports geldt daarnaast een rate limit met een in-memory teller (drie downloads per uur per gebruiker).</p>
        <p><strong>K. Abonnement en betaling</strong> — als de owner een abonnement afsluit, bewaart Rostr op de bedrijfsrecord de Stripe klant-ID en de Stripe abonnements-ID. De invoer en verwerking van kaartgegevens gebeurt bij Stripe zelf; Rostr slaat geen volledige kaartnummers, CVC-codes of andere ruwe betaalgegevens op.</p>
      </>
    ),
  },
  {
    id: "herkomst",
    title: "Waar komen deze gegevens vandaan?",
    body: (
      <>
        <p>Afhankelijk van de functionaliteit komen gegevens uit één of meer van de volgende bronnen:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Jij als gebruiker</strong> — registratie, profiel bewerken, verlof aanvragen, uren indienen, notificatievoorkeuren aanpassen.</li>
          <li><strong>De werkgever of eigenaar</strong> — bedrijfsinstellingen, de aangemaakte accounts, de uitnodigingen, de rolverdeling en de privacy-instellingen.</li>
          <li><strong>Managers en teamleiders</strong> — roosterinvoer, goedkeuring van uren, beslissingen op verlof- en dienstruilverzoeken.</li>
          <li><strong>Het uitnodigingsproces</strong> — als je door een owner of manager per e-mail wordt uitgenodigd, staan je rol, locatie en team meestal al klaar voordat je zelf inlogt.</li>
          <li><strong>Automatisch tijdens gebruik van de app</strong> — auditlogregels, gelezen/ongelezen-status van meldingen, sessiegegevens.</li>
          <li><strong>Google</strong> — als je met je Google-account inlogt, ontvangen we in ieder geval je e-mailadres, naam en (indien beschikbaar) profielfoto vanuit de Google-sessie.</li>
        </ul>
      </>
    ),
  },
  {
    id: "doeleinden",
    title: "Waarvoor worden gegevens gebruikt?",
    body: (
      <>
        <p>Rostr gebruikt persoonsgegevens voor de volgende doeleinden:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>het aanmaken en beheren van je gebruikersaccount;</li>
          <li>authenticatie via wachtwoord of Google, plus sessie- en tokenbeheer;</li>
          <li>het bepalen van rollen en toegangsrechten binnen je bedrijf;</li>
          <li>het koppelen van medewerkers aan hun bedrijf, locatie en team;</li>
          <li>roosterplanning en publicatie van diensten;</li>
          <li>verlof- en afwezigheidsbeheer;</li>
          <li>urenadministratie (indiening, controle, goedkeuring);</li>
          <li>dienstruilen tussen medewerkers;</li>
          <li>notificaties in de app en, indien geactiveerd, via web push;</li>
          <li>rapportages en overzichten voor de werkgever;</li>
          <li>abonnements- en factuurverwerking via Stripe (voor de owner);</li>
          <li>support en klantcontact;</li>
          <li>beveiliging, foutopsporing en misbruikpreventie;</li>
          <li>auditbaarheid van beheer- en privacygevoelige acties;</li>
          <li>het exporteren van persoons- of bedrijfsgegevens op verzoek;</li>
          <li>het uitvoeren van account- of bedrijfsverwijderingen.</li>
        </ul>
        <p>Rostr verkoopt geen persoonsgegevens en gebruikt ze niet voor advertentie- of marketingprofielen.</p>
      </>
    ),
  },
  {
    id: "grondslagen",
    title: "Juridische grondslagen",
    body: (
      <>
        <p>
          Rostr en de organisatie achter Rostr moeten vóór de commerciële
          lancering per verwerking een juridische grondslag vaststellen. Deze
          concepttekst gaat daarop vooruit en formuleert daarom richtinggevend
          in plaats van definitief.
        </p>
        <p><strong>Personeelsgegevens die je werkgever via Rostr verwerkt.</strong> Voor deze verwerkingen bepaalt jouw werkgever (of het bedrijf waarvoor je werkt) waarom die gegevens nodig zijn en welke toepasselijke juridische grondslag geldt (bijvoorbeeld arbeidsovereenkomst, personeelsadministratie of wettelijke verplichting). Rostr levert daarvoor de softwaredienst volgens de uiteindelijk vast te leggen juridische rolverdeling.</p>
        <p><strong>Verwerkingen waarvoor de organisatie achter Rostr zelf doeleinden bepaalt.</strong> Denk aan facturatie richting het bedrijf, accountbeveiliging, support, misbruikpreventie en interne technische administratie van het platform. Voor deze verwerkingen zijn afhankelijk van de context grondslagen als de uitvoering van een overeenkomst, een wettelijke verplichting of een gerechtvaardigd belang mogelijk relevant. De definitieve grondslag per verwerking wordt vóór de commerciële lancering juridisch getoetst.</p>
        <p><strong>Google Sign-In.</strong> Google Sign-In is optioneel; gegevens worden alleen via deze route ontvangen wanneer je zelf voor Google-inloggen kiest. Welke juridische grondslag hier definitief bij past — bijvoorbeeld uitvoering van een overeenkomst, toestemming of een andere grondslag — wordt vóór de commerciële lancering gevalideerd. Toestemming wordt niet gebruikt als standaardgrondslag voor alle verwerkingen binnen Rostr.</p>
      </>
    ),
  },
  {
    id: "gevoelig",
    title: "Ziekte, verzuim en gevoelige gegevens",
    body: (
      <>
        <p>
          Informatie dat iemand ziek is, verdient extra bescherming. Rostr kent
          een administratieve afwezigheidscategorie "ziek" (naast vakantie,
          persoonlijk, onbetaald en overig). Voor een normale ziekmelding
          heeft Rostr <strong>geen diagnose, oorzaak, behandeling, medicatie
          of symptomen nodig</strong>.
        </p>
        <p>
          Bij het type <em>ziek</em> is het vrije reden-tekstveld in de
          verlofaanvraag verborgen; in plaats daarvan verschijnt een uitleg
          dat je hier geen medische details hoort in te vullen. Server-side
          negeert Rostr bovendien een eventueel meegestuurd vrij tekstveld
          bij dit type, zodat medische informatie ook via een omweg niet in
          het systeem terechtkomt.
        </p>
        <p>
          Rostr is <strong>geen medisch dossier</strong> en vervangt geen
          bedrijfsarts of arbodienstverlener. De correcte afhandeling van
          ziekmeldingen — inclusief eventuele communicatie met een
          bedrijfsarts, arbodienst of loondoorbetalingsproces — ligt bij de
          werkgever. Rostr is daar geen partij bij.
        </p>
      </>
    ),
  },
  {
    id: "zichtbaarheid",
    title: "Wie kan welke gegevens zien?",
    body: (
      <>
        <p>De zichtbaarheid van gegevens binnen een bedrijf hangt af van drie dingen: je rol, de bedrijfsinstellingen en de individuele privacy-overrides die de owner voor jou heeft ingesteld.</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Owner</strong> — breedste rechten binnen het bedrijf; kan onder meer bedrijfsinstellingen, rechten, abonnement en de account- of bedrijfsverwijdering beheren.</li>
          <li><strong>Manager</strong> — alleen volgens de toegewezen scope (locaties, teams, medewerkers) en de rechten die de owner heeft ingesteld. Managers zien buiten hun scope geen operationele gegevens.</li>
          <li><strong>Teamlead</strong> — helpt een team beheren binnen de grenzen die owner of manager instelt. Een teamlead ziet niet automatisch alle medewerkers of alle locaties.</li>
          <li><strong>Employee</strong> — ziet vooral het eigen rooster, verlof, uren, notificaties en beperkte informatie over collega's afhankelijk van de bedrijfsinstellingen.</li>
        </ul>
        <p>
          De <strong>Privacy Matrix</strong> legt per veld vast wie het
          standaard mag zien. Denk aan velden zoals e-mailadres, telefoonnummer,
          geboortedatum, functie, afdeling of profielfoto. De owner kan
          daarnaast per medewerker <strong>individuele privacy-overrides</strong>
          instellen: bijvoorbeeld "telefoonnummer standaard alleen voor
          managers, maar deze specifieke medewerker wil zijn/haar nummer wel
          voor collega's zichtbaar maken". De zichtbaarheid wordt server-side
          afgedwongen, niet alleen in de UI.
        </p>
      </>
    ),
  },
  {
    id: "externe-diensten",
    title: "Externe diensten die Rostr gebruikt",
    body: (
      <>
        <p>Op basis van de huidige technische configuratie worden persoonsgegevens onder meer verwerkt via de volgende externe diensten:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Hosting / infrastructuur</strong> — Rostr draait op de door Emergent beheerde container-infrastructuur.</li>
          <li><strong>Database</strong> — de operationele gegevens worden opgeslagen in een MongoDB-instantie die binnen dezelfde infrastructuur wordt gedraaid.</li>
          <li><strong>Stripe</strong> — voor abonnementen en betalingen door de owner. Kaartgegevens worden bij Stripe ingevoerd en verwerkt; Rostr bewaart alleen de Stripe klant-ID en abonnements-ID op de bedrijfsrecord.</li>
          <li><strong>Emergent-managed Google Sign-In</strong> — voor gebruikers die met hun Google-account willen inloggen. Bij een succesvolle Google-login ontvangt Rostr in ieder geval e-mailadres, naam en (indien beschikbaar) profielfoto.</li>
          <li><strong>Emergent-managed e-mailproxy</strong> — voor transactionele e-mails, zoals uitnodigingsmails en accountgerelateerde mails. Deze mails worden verzonden via een door Emergent beheerde Resend-integratie.</li>
          <li><strong>Web Push</strong> — Rostr heeft een eigen VAPID-sleutelpaar en stuurt push-notificaties rechtstreeks vanaf de eigen backend naar de browser-push-endpoints. Push-abonnementsgegevens worden per gebruiker in Rostr opgeslagen.</li>
        </ul>
        <p>
          Diensten die nog niet live zijn — bijvoorbeeld externe
          documentopslag — worden hier bewust <strong>niet</strong> vermeld.
          Zodra er iets aan deze lijst verandert, wordt deze verklaring
          bijgewerkt.
        </p>
      </>
    ),
  },
  {
    id: "internationaal",
    title: "Internationale verwerking",
    body: (
      <>
        <p>
          De precieze regio waarin Rostr wordt gehost, en de regio's waarin de
          gebruikte externe diensten hun infrastructuur draaien, worden vóór
          de commerciële lancering formeel vastgelegd. Rostr streeft ernaar de
          verwerking zoveel mogelijk binnen de EU/EER te houden.
        </p>
        <p>
          We doen op dit moment geen absolute uitspraak zoals "alle gegevens
          blijven binnen Nederland" of "alle gegevens blijven binnen de EU",
          omdat dat afhankelijk is van infrastructuurkeuzes en contractuele
          afspraken die nog niet definitief zijn. Zodra hier duidelijkheid over
          is, wordt dat concreet in deze verklaring beschreven, inclusief
          eventuele doorgiftewaarborgen.
        </p>
      </>
    ),
  },
  {
    id: "bewaartermijnen",
    title: "Bewaartermijnen",
    body: (
      <>
        <p>Onderstaande tabel beschrijft hoe Rostr op dit moment technisch met bewaartermijnen omgaat. Daarna volgt kort het verschil met wettelijke bewaarplichten die op de werkgever kunnen rusten.</p>
        <RetentionTable
          headers={["Gegevenscategorie", "Huidige technische behandeling"]}
          rows={[
            ["Account / profiel", "Actief zolang je account bestaat. Bij self-delete worden direct identificerende persoonsgegevens verwijderd of vervangen; resterende interne referenties kunnen worden gepseudonimiseerd (zie sectie 13)."],
            ["Wachtwoord-hash", "Alleen aanwezig als je met e-mail/wachtwoord bent geregistreerd. Volledig verwijderd bij self-delete."],
            ["Refresh-tokens", "Ingetrokken bij self-delete. Verlopen daarnaast van nature op basis van hun eigen levensduur."],
            ["Auditlog", "720 dagen (ongeveer 24 maanden). Elke auditregel krijgt een retain_until-datum van 720 dagen vooruit; MongoDB verwijdert de regel via een TTL-index automatisch nadat die datum voorbij is."],
            ["Roosters, uren, verlof, dienstruilen", "Kunnen na een individuele accountverwijdering behouden blijven als historische bedrijfsadministratie. Ze blijven gekoppeld aan een gepseudonimiseerde interne referentie (\"Verwijderde medewerker\") en worden niet aan een andere medewerker gekoppeld."],
            ["Notificaties", "Blijven gekoppeld aan de ontvanger tot ze door de gebruiker worden gewist. Bij accountverwijdering worden de notificaties van die gebruiker verwijderd. Bij bedrijfsverwijdering worden alle notificaties van dat bedrijf via de verwijderprocedure gepurged."],
            ["Uitnodigingen", "Krijgen bij aanmaken een expires_at-datum (standaard 7 dagen; per bedrijf instelbaar). Zolang een uitnodiging pending is en nog niet is verlopen, blijft de code beschikbaar. Zodra een uitnodiging is geclaimd (used_at), verlopen (expires_at in het verleden) of ingetrokken (revoked_at), krijgt de record een retain_until-datum van 30 dagen na dat terminal moment. Een achtergrondtaak stampt elke 6 uur nieuw-verlopen uitnodigingen die geen claim of intrekking hebben gekregen, en de MongoDB TTL-index verwijdert de record automatisch nadat retain_until is verstreken."],
            ["Bedrijfsverwijdering", "30 dagen grace period; daarna komt het bedrijf in aanmerking voor definitieve verwijdering volgens de verwijderprocedure van Rostr."],
          ]}
        />
        <p>
          De bovenstaande termijnen zijn de standaard-retentie die Rostr
          technisch afdwingt of ondersteunt. Op de werkgever kunnen
          <strong> daarnaast </strong> wettelijke bewaarplichten rusten
          (bijvoorbeeld voor loon- of urenadministratie). Waar dat het geval
          is, prevaleren die wettelijke termijnen — de verantwoordelijkheid
          voor het naleven daarvan ligt bij de werkgever.
        </p>
      </>
    ),
  },
  {
    id: "account-verwijderen",
    title: "Account verwijderen",
    body: (
      <>
        <p>Je kunt je eigen account verwijderen vanuit je profiel. De flow is als volgt:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>Je start het verzoek in je profiel.</li>
          <li>Je moet je huidige wachtwoord opnieuw invoeren om het verzoek te bevestigen (re-authenticatie). Bij Google-only-accounts gebeurt de check op basis van de bestaande sessie.</li>
          <li>Je e-mailadres wordt vervangen door een intern systeemadres (<code>deleted-&#123;id&#125;@rostr.local</code>) en je naam wordt vervangen door "Verwijderde medewerker". Dit is <strong>pseudonimisering</strong>, geen volledige anonimisering: de <code>&#123;id&#125;</code> blijft een interne referentie waaraan historische bedrijfsrecords gekoppeld kunnen zijn.</li>
          <li>Je persoonlijke velden — telefoon, profielfoto, geboortedatum, noodcontact, persoonlijke notities en notificatievoorkeuren — worden op <code>null</code> gezet.</li>
          <li>Je wachtwoord-hash wordt volledig uit de record verwijderd.</li>
          <li>Alle refresh-tokens van je account worden ingetrokken; je wordt effectief uitgelogd op alle apparaten.</li>
          <li>Je notificaties en eventuele web-push-abonnementen worden verwijderd.</li>
          <li>Historische bedrijfsrecords — bijvoorbeeld uren die je hebt ingediend of diensten waarop je was gepland — kunnen behouden blijven voor de administratie van je (voormalige) werkgever. Ze blijven gekoppeld aan je gepseudonimiseerde profiel en worden niet aan een andere medewerker gekoppeld.</li>
          <li>De verwijderactie wordt vastgelegd in het auditlog.</li>
        </ol>
        <p>
          <strong>Uitzondering:</strong> als je als enige actieve owner in een
          bedrijf staat, kun je je eigen account niet zomaar verwijderen. In
          dat geval moet eerst het eigenaarschap worden overgedragen (nog toe
          te voegen functionaliteit) of het bedrijf worden verwijderd, zodat
          er nooit een bedrijf zonder owner overblijft.
        </p>
      </>
    ),
  },
  {
    id: "bedrijf-verwijderen",
    title: "Bedrijf verwijderen",
    body: (
      <>
        <p>Alleen de owner van een bedrijf kan het bedrijf verwijderen. De flow is:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>De owner start de verwijdering vanuit de instellingen.</li>
          <li>De owner moet ter bevestiging de exacte bedrijfsnaam intypen en, indien er een wachtwoord is, opnieuw het wachtwoord invoeren.</li>
          <li>Het bedrijf krijgt de status <code>deletion_state = pending</code> met een geplande einddatum 30 dagen in de toekomst.</li>
          <li>Vanaf dat moment blokkeert Rostr alle normale tenanttoegang met een HTTP 423-response en de code <code>tenant_inactive</code>. Alleen accountflows, exports en het annuleren of aanvragen van de verwijdering blijven bereikbaar, zodat gebruikers hun gegevens nog kunnen exporteren of het proces kunnen terugdraaien.</li>
          <li>Binnen de 30 dagen kan de owner het verzoek annuleren; het bedrijf keert dan terug naar de normale actieve status.</li>
          <li>Na afloop van de 30 dagen komt het bedrijf in aanmerking voor definitieve verwijdering volgens de verwijderprocedure van Rostr. Deze definitieve verwijdering wordt niet automatisch geactiveerd op de exacte einddatum, maar loopt via een beheersproces.</li>
        </ol>
      </>
    ),
  },
  {
    id: "export",
    title: "Data-export",
    body: (
      <>
        <p><strong>A. Mijn gegevens downloaden</strong> — <code>GET /api/gdpr/export/me.zip</code>. Vanuit je profiel haal je een ZIP-bestand op met alleen je eigen persoonsgegevens. De ZIP bevat:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><code>profile.json</code> — persoonlijke velden van je account (naam, e-mailadres, taal, thema, telefoon, geboortedatum, noodcontact, functie, afdeling, locatie/team-ID en notificatievoorkeuren);</li>
          <li><code>shifts.json</code> — de diensten waarop je was ingepland;</li>
          <li><code>leave.json</code> — je verlofaanvragen;</li>
          <li><code>swaps.json</code> — dienstruilverzoeken waar je bij betrokken bent;</li>
          <li><code>timesheets.json</code> — je urenregistraties;</li>
          <li><code>notifications.json</code> — meldingen die aan jou zijn gericht;</li>
          <li><code>_manifest.json</code> — een korte notitie over de export.</li>
        </ul>
        <p>De export bevat géén wachtwoord-hashes, refresh-tokens, sessie-secrets of andere secrets, en géén persoonsgegevens van andere medewerkers. Personal exports zijn beperkt tot drie downloads per uur per gebruiker.</p>
        <p><strong>B. Bedrijfsgegevens exporteren</strong> — <code>GET /api/gdpr/export/company.zip</code>. Alleen owners kunnen deze export uitvoeren. De ZIP bevat:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><code>company.json</code> — de bedrijfsrecord (zonder secrets);</li>
          <li><code>locations.json</code>, <code>teams.json</code> — vestigingen en teams;</li>
          <li><code>users.csv</code> — een beperkte kolomlijst per medewerker (id, naam, e-mailadres, rol, locatie/team-ID en <code>deleted_at</code> indien van toepassing);</li>
          <li><code>shifts.csv</code>, <code>leave.csv</code>, <code>swaps.csv</code> — de operationele gegevens;</li>
          <li><code>invitations.csv</code> — verstuurde uitnodigingen;</li>
          <li><code>audit.csv</code> — de auditlogregels van de laatste 12 maanden;</li>
          <li><code>_manifest.json</code>.</li>
        </ul>
        <p>Ook hier: géén wachtwoord-hashes, refresh-tokens of andere secrets. Bedrijfsexport is eveneens beperkt tot drie downloads per uur per bedrijf.</p>
      </>
    ),
  },
  {
    id: "rechten",
    title: "Rechten van gebruikers",
    body: (
      <>
        <p>
          Op grond van de privacywetgeving heb je in beginsel de volgende
          rechten. Deze rechten zijn niet absoluut: ze kunnen worden beperkt
          door wettelijke bewaarplichten van de werkgever, door de rechten en
          vrijheden van anderen of door aantoonbare gerechtvaardigde belangen.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Informatie</strong> — je hebt recht op duidelijke informatie over hoe Rostr en je werkgever met je gegevens omgaan. Deze verklaring is daar een onderdeel van.</li>
          <li><strong>Inzage</strong> — de meeste eigen gegevens zijn direct in de app zichtbaar; met de personal export haal je een gestructureerd overzicht op.</li>
          <li><strong>Correctie</strong> — veel eigen velden (naam, telefoon, geboortedatum, voorkeuren) kun je zelf aanpassen in je profiel. Voor gegevens die je werkgever over jou vastlegt (bijvoorbeeld functie of team) kun je bij je werkgever aankloppen.</li>
          <li><strong>Verwijdering</strong> — je kunt je account verwijderen (zie sectie 13). Historische bedrijfsrecords kunnen behouden blijven.</li>
          <li><strong>Beperking en bezwaar</strong> — waar toepasselijk kun je vragen om de verwerking te beperken of bezwaar maken tegen bepaalde verwerkingen. Deze verzoeken worden per geval beoordeeld.</li>
          <li><strong>Dataportabiliteit</strong> — waar toepasselijk kun je een machineleesbare export vragen; voor je eigen gegevens is dat de personal export.</li>
          <li><strong>Klacht bij toezichthouder</strong> — je hebt het recht een klacht in te dienen bij de bevoegde privacytoezichthouder. In Nederland is dat de Autoriteit Persoonsgegevens (<a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="underline">autoriteitpersoonsgegevens.nl</a>).</li>
        </ul>
        <p>
          Voor personeelsgegevens die je werkgever via Rostr verwerkt, is die
          werkgever in veel gevallen het eerste aanspreekpunt voor de
          uitoefening van deze rechten. Rostr ondersteunt jou en je werkgever
          waar we kunnen (bijvoorbeeld met exports en accountverwijdering),
          maar treedt niet in de plaats van je werkgever bij vragen die
          primair over de arbeidsrelatie gaan.
        </p>
      </>
    ),
  },
  {
    id: "geautomatiseerd",
    title: "Geautomatiseerde besluitvorming",
    body: (
      <>
        <p>
          Rostr neemt op dit moment geen besluiten uitsluitend op basis van
          geautomatiseerde verwerking of profilering die je juridisch of
          anderszins aanzienlijk treffen. Roosters worden door managers of
          teamleiders vastgesteld en gepubliceerd; verlofaanvragen en uren
          worden door mensen beoordeeld; individuele privacy-overrides worden
          door de owner ingesteld.
        </p>
        <p>
          Automatische processen die wel bestaan — bijvoorbeeld het
          tijdgestuurd verwijderen van auditregels na 24 maanden — hebben geen
          effect op individuele beslissingen over personen.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies, localStorage en sessie-opslag",
    body: (
      <>
        <p>In je browser gebruikt Rostr:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>HTTP-only cookies voor authenticatie</strong> — een <code>access_token</code>- en <code>refresh_token</code>-cookie (beide HttpOnly + Secure) om je ingelogd te houden. Deze cookies zijn niet uitleesbaar vanuit JavaScript.</li>
          <li><strong>Een <code>csrf_token</code>-cookie</strong> — bewust wél leesbaar zodat de app een matchende <code>X-CSRF-Token</code>-header kan meesturen bij state-wijzigende verzoeken. Deze token bevat geen persoonsgegevens en geen sessiegeheim.</li>
          <li><strong>localStorage</strong> — uitsluitend voor voorkeurgegevens die de app snel nodig heeft, zoals taalinstelling (<code>rooster.lang</code>), thema (<code>rostr.theme</code>), "welkom-onboarding gezien", "wat is nieuw gezien", de dashboard-widgetconfiguratie en de dismiss-status van de install-banner. Je access- en refresh-token worden hier bewust <em>niet</em> in bewaard.</li>
          <li><strong>sessionStorage</strong> — voor de tijdelijke intent en payload tijdens het inloggen met Google, en voor "welkom-scherm al getoond in deze sessie".</li>
        </ul>
        <p>
          Er is op dit moment <strong>geen</strong> tracking-, marketing- of
          advertentiecookie in gebruik en geen third-party
          analytics-integratie in de app. Als daar in de toekomst iets aan
          verandert, wordt deze verklaring bijgewerkt en zullen we — waar
          wettelijk vereist — toestemming vragen.
        </p>
      </>
    ),
  },
  {
    id: "beveiliging",
    title: "Beveiliging",
    body: (
      <>
        <p>Rostr heeft onder meer de volgende maatregelen ingericht:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Authenticatie via wachtwoord (met bcrypt-hashing) of via Emergent-managed Google Sign-In.</li>
          <li>Rolgebaseerde rechten (Owner, Manager, Teamlead, Employee) die server-side worden afgedwongen op elke relevante API-route.</li>
          <li>Tenant-isolatie: een gebruiker in bedrijf A kan geen gegevens van bedrijf B zien of opvragen.</li>
          <li>De Privacy Matrix en individuele privacy-overrides om zichtbaarheid van velden per rol en per medewerker fijn te sturen.</li>
          <li>Rate limiting op gevoelige acties, bijvoorbeeld maximaal drie personal exports per uur per gebruiker en drie bedrijfsexports per uur per bedrijf.</li>
          <li>Re-authenticatie via wachtwoord bij destructieve acties zoals account- of bedrijfsverwijdering.</li>
          <li>Auditlogging van beheeracties met een technische bewaartermijn van 24 maanden.</li>
          <li>Wachtwoord-hashes, refresh-tokens en andere secrets worden nooit meegeleverd in exports; het serialisatiepad strip deze velden expliciet.</li>
        </ul>
        <p>
          Rostr claimt <strong>geen</strong> formele beveiligingscertificeringen
          die niet zijn afgegeven (zoals SOC 2 of ISO 27001) en gebruikt geen
          termen als "bank-level security" of "100% veilig". Wanneer daar in
          de toekomst iets in verandert, wordt deze verklaring bijgewerkt.
        </p>
      </>
    ),
  },
  {
    id: "incidenten",
    title: "Beveiligingsincidenten en datalekken",
    body: (
      <>
        <p>
          Als Rostr bij de verwerking van klantgegevens optreedt als
          verwerker en een beveiligingsincident constateert, informeert Rostr
          de betreffende klant/verwerkingsverantwoordelijke volgens de
          toepasselijke afspraken zonder onnodige vertraging. De klant
          beoordeelt vervolgens eventuele meldplichten richting toezichthouder
          en betrokkenen; Rostr ondersteunt daar waar we kunnen.
        </p>
        <p>
          Voor incidenten rond gegevens waarvoor de organisatie achter Rostr
          zelf verantwoordelijk is (bijvoorbeeld accountbeveiliging of
          factuurgegevens), beoordeelt Rostr zelf de eventuele meldplicht.
        </p>
        <p>
          Rostr claimt uitdrukkelijk <strong>geen</strong> 24/7 security
          operations center of vaste responstijden. De volledige, formele
          incident- en datalekprocedure — inclusief interne
          verantwoordelijkheden, escalatie en communicatie-templates —
          wordt vóór de commerciële lancering vastgelegd samen met de
          juridische review.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact en wijzigingen",
    body: (
      <>
        <p>
          Vragen over deze privacyverklaring, je gegevens, een export of
          accountverwijdering kun je sturen naar{" "}
          <a
            href="mailto:rosterhelp@gmail.com"
            className="underline hover:text-[hsl(var(--primary))]"
          >
            rosterhelp@gmail.com
          </a>. Voor algemene informatie over Rostr kun je terecht bij{" "}
          <a
            href="mailto:info.roster@gmail.com"
            className="underline hover:text-[hsl(var(--primary))]"
          >
            info.roster@gmail.com
          </a>. Andere adressen — bijvoorbeeld op @rostr.eu of een
          functionaris voor gegevensbescherming — worden pas gecommuniceerd
          zodra die daadwerkelijk zijn aangemaakt en bevestigd.
        </p>
        <p>
          Rostr kan deze privacyverklaring aanpassen wanneer functionaliteit
          verandert of wanneer wet- en regelgeving daarom vraagt. De datum
          bovenaan de pagina geeft aan wanneer de tekst voor het laatst is
          bijgewerkt.
        </p>
      </>
    ),
  },
];

/* ================================================================== */
/*  PRIVACY — English                                                 */
/* ================================================================== */
const PRIVACY_EN = [
  {
    id: "wat-is-rostr",
    title: "What is Rostr?",
    body: (
      <>
        <p>Rostr is an online SaaS platform for workforce scheduling. Organisations use Rostr amongst other things to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>manage employees (name, contact details, job title, team, location, role);</li>
          <li>plan and publish schedules and shifts;</li>
          <li>record and review leave and absence;</li>
          <li>submit, review and approve hours worked;</li>
          <li>request and approve shift swaps;</li>
          <li>send notifications for relevant events;</li>
          <li>build reports from the data above;</li>
          <li>manage teams and multiple locations separately.</li>
        </ul>
        <p>
          The owner of a company can optionally take out a subscription via
          Stripe. Rostr also offers Google sign-in as an authentication option
          alongside email/password sign-in.
        </p>
      </>
    ),
  },
  {
    id: "voor-wie",
    title: "Who does this privacy statement apply to?",
    body: (
      <>
        <p>This statement describes how personal data is processed when using Rostr. It applies to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>owners who create and manage a company on Rostr;</li>
          <li>managers and team leads who manage part of the workforce or scheduling within that company;</li>
          <li>employees who use Rostr to view their schedule, request leave, submit hours or handle shift swaps;</li>
          <li>people who have been invited by email to a company and have not yet completed the sign-up process.</li>
        </ul>
        <p>
          The statement expressly does not cover: data your employer processes
          outside of Rostr, or data you share on other systems.
        </p>
      </>
    ),
  },
  {
    id: "rollen-avg",
    title: "Processing roles: company and Rostr",
    body: (
      <>
        <p>
          Rostr is a B2B platform. For a large part of the personnel data that
          ends up in Rostr — for example who works which shift, which leave
          has been requested or which hours were submitted — your employer
          (or the company you work for) determines why that data is recorded
          and used within the organisation. Rostr provides the software
          service that makes this processing possible.
        </p>
        <p>For certain activities of Rostr itself, the legal role may differ. Think of:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>account security (including sign-in attempts, session handling, re-authentication);</li>
          <li>support requests that you send us directly;</li>
          <li>billing towards the company via our payment provider;</li>
          <li>abuse prevention and troubleshooting;</li>
          <li>internal technical administration of the platform.</li>
        </ul>
        <p>
          The definitive legal role split — including who is a
          controller and who is a processor for each processing activity —
          will be formalised before commercial launch, in dialogue with the
          companies using Rostr. That is why this statement is a draft
          version, and does not claim that Rostr is always a processor or
          always a controller.
        </p>
      </>
    ),
  },
  {
    id: "gegevens",
    title: "Which personal data does Rostr process?",
    body: (
      <>
        <p>The categories below are based on the data models that actually exist in the code today.</p>
        <p><strong>A. Account and identification data</strong> — email address, name, user ID, role (owner / manager / team lead / employee), authentication provider (password or Google), password hash (only if you registered with a password), refresh tokens for session management, and account creation timestamp.</p>
        <p><strong>B. Work and organisation data</strong> — company, location ID, team ID, job title (optional), department (optional), Rostr role, contract hours per week (if entered by the employer), and for managers/team leads the scope of locations and employees they may manage.</p>
        <p><strong>C. Contact and profile data</strong> — insofar as filled in by the user or the employer: phone number, date of birth, profile picture (avatar), emergency contact, language preference and display theme (light / dark / system).</p>
        <p><strong>D. Schedule data</strong> — per shift: date, start time, end time, break (where applicable), location, team, linked employee (or "open shift" if no employee is assigned yet), role label, notes and publication status (draft or published), plus a creation timestamp.</p>
        <p><strong>E. Time tracking</strong> — per submitted line: shift ID, actual start and end time, an optional note, the status (submitted / approved / rejected) and the manager remark on approval or rejection.</p>
        <p><strong>F. Leave and absence</strong> — per leave request: start and end date, type (vacation, sick, personal, unpaid or other), an optional non-medical administrative reason (free text, only for leave types other than sick), status (pending / approved / rejected), manager note, requester and creation time. For <em>sick</em>, Rostr stores no free-text field; only that someone is reporting sick and the period. See also section 8.</p>
        <p><strong>G. Shift swaps</strong> — per swap request: requester, the shift involved, an optional target employee, an optional message, status (pending / accepted / declined / approved / cancelled) and the timestamp of the last decision.</p>
        <p><strong>H. Notifications</strong> — per notification: recipient, a title, an optional body text, the type, an optional link into the app, the read/unread status and the creation time. If you enabled web push, we also store the push subscription (endpoint plus the browser encryption keys) needed to reach your device.</p>
        <p><strong>I. Reporting data</strong> — Rostr does not store reports separately. They are composed on request from existing operational data (schedule, hours, leave).</p>
        <p><strong>J. Technical and security data</strong> — administrative actions (for example creating or deleting an employee, approving hours, exporting data, requesting or cancelling a company deletion) are recorded in an audit log with timestamp, actor, action type and target. For sensitive actions such as exports, a rate limit applies with an in-memory counter (three downloads per hour per user).</p>
        <p><strong>K. Subscription and payment</strong> — if the owner takes out a subscription, Rostr stores the Stripe customer ID and Stripe subscription ID on the company record. The input and processing of card data happens at Stripe itself; Rostr does not store full card numbers, CVC codes or other raw payment data.</p>
      </>
    ),
  },
  {
    id: "herkomst",
    title: "Where does this data come from?",
    body: (
      <>
        <p>Depending on the functionality, data comes from one or more of the following sources:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>You as a user</strong> — registration, editing your profile, requesting leave, submitting hours, changing notification preferences.</li>
          <li><strong>The employer or owner</strong> — company settings, the accounts created, invitations, role distribution and privacy settings.</li>
          <li><strong>Managers and team leads</strong> — schedule input, approval of hours, decisions on leave and shift swap requests.</li>
          <li><strong>The invitation process</strong> — if an owner or manager invites you by email, your role, location and team are usually pre-set before you log in yourself.</li>
          <li><strong>Automatically during use of the app</strong> — audit log entries, read/unread status of notifications, session data.</li>
          <li><strong>Google</strong> — if you sign in with your Google account, we at least receive your email address, name and (if available) profile picture from the Google session.</li>
        </ul>
      </>
    ),
  },
  {
    id: "doeleinden",
    title: "What is the data used for?",
    body: (
      <>
        <p>Rostr uses personal data for the following purposes:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>creating and managing your user account;</li>
          <li>authentication via password or Google, and session/token management;</li>
          <li>determining roles and access rights within your company;</li>
          <li>linking employees to their company, location and team;</li>
          <li>schedule planning and shift publication;</li>
          <li>leave and absence management;</li>
          <li>hours administration (submission, review, approval);</li>
          <li>shift swaps between employees;</li>
          <li>notifications inside the app and, when enabled, via web push;</li>
          <li>reports and overviews for the employer;</li>
          <li>subscription and billing via Stripe (for the owner);</li>
          <li>support and customer contact;</li>
          <li>security, troubleshooting and abuse prevention;</li>
          <li>auditability of administrative and privacy-sensitive actions;</li>
          <li>exporting personal or company data on request;</li>
          <li>carrying out account or company deletions.</li>
        </ul>
        <p>Rostr does not sell personal data and does not use it for advertising or marketing profiles.</p>
      </>
    ),
  },
  {
    id: "grondslagen",
    title: "Legal grounds",
    body: (
      <>
        <p>
          Rostr and the organisation behind Rostr must, before commercial
          launch, establish a legal ground for each processing activity. This
          draft text anticipates that work and therefore stays indicative
          rather than definitive.
        </p>
        <p><strong>Personnel data your employer processes via Rostr.</strong> For these processing activities, your employer (or the company you work for) determines why the data is needed and which legal ground applies (for example the employment contract, personnel administration or a statutory obligation). Rostr provides the software service under the legal role split that will ultimately be recorded.</p>
        <p><strong>Processing where the organisation behind Rostr sets the purposes itself.</strong> Think of billing towards the company, account security, support, abuse prevention and internal technical administration of the platform. Depending on the context, grounds such as performance of a contract, a statutory obligation or a legitimate interest may be relevant. The definitive ground per processing activity will be legally reviewed before commercial launch.</p>
        <p><strong>Google Sign-In.</strong> Google Sign-In is optional; data is only received via this route when you choose to sign in with Google yourself. Which legal ground definitively fits here — for example performance of a contract, consent or another ground — will be validated before commercial launch. Consent is not used as a default ground for all processing within Rostr.</p>
      </>
    ),
  },
  {
    id: "gevoelig",
    title: "Sickness, absence and sensitive data",
    body: (
      <>
        <p>
          Information that someone is sick deserves additional protection.
          Rostr provides an administrative absence category "sick" (alongside
          vacation, personal, unpaid and other). For a normal sick report,
          Rostr <strong>does not need a diagnosis, cause, treatment,
          medication or symptoms</strong>.
        </p>
        <p>
          When the type is <em>sick</em>, the free-text reason field on the
          leave request is hidden; a short explanation appears instead stating
          that medical details should not be entered. On the server, Rostr
          also ignores any free-text reason submitted for this type, so
          medical information cannot end up in the system via a workaround.
        </p>
        <p>
          Rostr is <strong>not a medical record system</strong> and does not
          replace an occupational physician or arbo service. The correct
          handling of sick reports — including any communication with an
          occupational physician, arbo service or continued-payment process —
          lies with the employer. Rostr is not a party to that.
        </p>
      </>
    ),
  },
  {
    id: "zichtbaarheid",
    title: "Who can see what?",
    body: (
      <>
        <p>Visibility of data within a company depends on three things: your role, the company settings and the individual privacy overrides the owner has configured for you.</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Owner</strong> — broadest rights within the company; can manage company settings, permissions, subscription and account/company deletion.</li>
          <li><strong>Manager</strong> — only according to the assigned scope (locations, teams, employees) and the permissions the owner has configured. Outside their scope, managers do not see operational data.</li>
          <li><strong>Team lead</strong> — helps manage a team within the limits set by the owner or manager. A team lead does not automatically see all employees or all locations.</li>
          <li><strong>Employee</strong> — primarily sees their own schedule, leave, hours, notifications and limited information about colleagues, depending on the company settings.</li>
        </ul>
        <p>
          The <strong>Privacy Matrix</strong> records per field who may see it
          by default. Think of fields such as email address, phone number,
          date of birth, job title, department or profile picture. The owner
          can also configure <strong>individual privacy overrides</strong> per
          employee — for example "phone number visible only to managers by
          default, but this specific employee wants their number to be visible
          to colleagues too". Visibility is enforced server-side, not only in
          the UI.
        </p>
      </>
    ),
  },
  {
    id: "externe-diensten",
    title: "External services used by Rostr",
    body: (
      <>
        <p>Based on the current technical configuration, personal data is amongst other things processed via the following external services:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Hosting / infrastructure</strong> — Rostr runs on container infrastructure managed by Emergent.</li>
          <li><strong>Database</strong> — operational data is stored in a MongoDB instance running within the same infrastructure.</li>
          <li><strong>Stripe</strong> — for subscriptions and payments by the owner. Card data is entered and processed at Stripe; Rostr only stores the Stripe customer ID and subscription ID on the company record.</li>
          <li><strong>Emergent-managed Google Sign-In</strong> — for users who want to sign in with their Google account. On a successful Google sign-in, Rostr at least receives email address, name and (if available) profile picture.</li>
          <li><strong>Emergent-managed email proxy</strong> — for transactional emails, such as invitation and account-related mails. These emails are sent via a Resend integration managed by Emergent.</li>
          <li><strong>Web push</strong> — Rostr has its own VAPID key pair and sends push notifications directly from its own backend to browser push endpoints. Push subscription data is stored per user in Rostr.</li>
        </ul>
        <p>
          Services that are not yet live — for example external document
          storage — are deliberately <strong>not</strong> listed here. As
          soon as anything changes in this list, this statement is updated.
        </p>
      </>
    ),
  },
  {
    id: "internationaal",
    title: "International processing",
    body: (
      <>
        <p>
          The exact region in which Rostr is hosted, and the regions in which
          the external services used run their infrastructure, will be
          formalised before commercial launch. Rostr aims to keep processing
          as much as possible within the EU/EEA.
        </p>
        <p>
          At this time we make no absolute statement such as "all data
          remains within the Netherlands" or "all data remains within the
          EU", because that depends on infrastructure choices and contractual
          arrangements that are not yet definitive. Once this becomes clear,
          it will be described concretely in this statement, including any
          transfer safeguards.
        </p>
      </>
    ),
  },
  {
    id: "bewaartermijnen",
    title: "Retention periods",
    body: (
      <>
        <p>The table below describes how Rostr technically handles retention today. It is followed by a short note on the difference with statutory retention obligations that may apply to the employer.</p>
        <RetentionTable
          headers={["Data category", "Current technical handling"]}
          rows={[
            ["Account / profile", "Active as long as your account exists. On self-delete, direct identifying personal data is removed or replaced; remaining internal references may be pseudonymised (see section 13)."],
            ["Password hash", "Only present if you registered with email/password. Removed completely on self-delete."],
            ["Refresh tokens", "Revoked on self-delete. They also expire naturally based on their own lifetime."],
            ["Audit log", "720 days (approximately 24 months). Each audit entry receives a retain_until date 720 days in the future; MongoDB automatically removes the entry via a TTL index after that date."],
            ["Schedules, hours, leave, swaps", "May be retained after an individual account deletion as historical company records. They remain linked to a pseudonymised internal reference (\"Deleted employee\") and are not reassigned to another employee."],
            ["Notifications", "Remain linked to the recipient until cleared by the user. On account deletion, that user's notifications are removed. On company deletion, all notifications of that company are purged via the deletion procedure."],
            ["Invitations", "Get an expires_at date on creation (default 7 days; configurable per company). While an invitation is pending and not yet past its expires_at, the code remains available. Once an invitation has been claimed (used_at), expired (expires_at in the past) or revoked (revoked_at), the record receives a retain_until date 30 days after that terminal moment. A background task sweeps newly-expired invitations that never got a claim or revoke every 6 hours, and MongoDB's TTL index automatically deletes the record after retain_until has passed."],
            ["Company deletion", "30-day grace period; after that the company becomes eligible for final removal via the deletion procedure of Rostr."],
          ]}
        />
        <p>
          These periods are the standard retention Rostr technically enforces
          or supports. In addition, statutory retention obligations may apply
          to the employer (for example for payroll or hours administration).
          Where that is the case, those statutory periods prevail — the
          responsibility for complying with them lies with the employer.
        </p>
      </>
    ),
  },
  {
    id: "account-verwijderen",
    title: "Deleting your account",
    body: (
      <>
        <p>You can delete your own account from your profile. The flow is:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>You start the request from your profile.</li>
          <li>You have to re-enter your current password to confirm the request (re-authentication). For Google-only accounts the check is based on the existing session.</li>
          <li>Your email address is replaced with an internal system address (<code>deleted-&#123;id&#125;@rostr.local</code>) and your name is replaced with "Deleted employee". This is <strong>pseudonymisation</strong>, not full anonymisation: the <code>&#123;id&#125;</code> remains an internal reference to which historical company records may be linked.</li>
          <li>Your personal fields — phone, profile picture, date of birth, emergency contact, personal notes and notification preferences — are set to <code>null</code>.</li>
          <li>Your password hash is fully removed from the record.</li>
          <li>All refresh tokens of your account are revoked; you are effectively signed out on all devices.</li>
          <li>Your notifications and any web push subscriptions are removed.</li>
          <li>Historical company records — for example hours you submitted or shifts you were assigned to — may be retained for the administration of your (former) employer. They remain linked to your pseudonymised profile and are not reassigned to another employee.</li>
          <li>The deletion action itself is recorded in the audit log.</li>
        </ol>
        <p>
          <strong>Exception:</strong> if you are the sole active owner in a
          company, you cannot simply delete your own account. In that case
          ownership must first be transferred (functionality to be added) or
          the company must be deleted, so that a company is never left
          without an owner.
        </p>
      </>
    ),
  },
  {
    id: "bedrijf-verwijderen",
    title: "Deleting a company",
    body: (
      <>
        <p>Only the owner of a company can delete the company. The flow is:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>The owner starts the deletion from settings.</li>
          <li>The owner must type the exact company name as confirmation and, if a password exists, re-enter the password.</li>
          <li>The company enters the state <code>deletion_state = pending</code> with a scheduled end date 30 days in the future.</li>
          <li>From that moment, Rostr blocks all normal tenant access with an HTTP 423 response and the code <code>tenant_inactive</code>. Only account flows, exports and cancelling/requesting the deletion remain reachable, so users can still export their data or reverse the process.</li>
          <li>Within the 30 days the owner can cancel the request; the company then returns to the normal active state.</li>
          <li>After the 30 days, the company becomes eligible for final removal via the deletion procedure of Rostr. This final removal is not automatically triggered on the exact end date but runs via an operational process.</li>
        </ol>
      </>
    ),
  },
  {
    id: "export",
    title: "Data export",
    body: (
      <>
        <p><strong>A. Download my data</strong> — <code>GET /api/gdpr/export/me.zip</code>. From your profile you can obtain a ZIP file with only your own personal data. The ZIP contains:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><code>profile.json</code> — personal fields of your account (name, email, language, theme, phone, date of birth, emergency contact, job title, department, location/team ID and notification preferences);</li>
          <li><code>shifts.json</code> — shifts you were scheduled on;</li>
          <li><code>leave.json</code> — your leave requests;</li>
          <li><code>swaps.json</code> — shift swap requests you are involved in;</li>
          <li><code>timesheets.json</code> — your time submissions;</li>
          <li><code>notifications.json</code> — notifications addressed to you;</li>
          <li><code>_manifest.json</code> — a short note about the export.</li>
        </ul>
        <p>The export contains no password hashes, refresh tokens, session secrets or other secrets, and no personal data of other employees. Personal exports are limited to three downloads per hour per user.</p>
        <p><strong>B. Export company data</strong> — <code>GET /api/gdpr/export/company.zip</code>. Only owners can perform this export. The ZIP contains:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><code>company.json</code> — the company record (without secrets);</li>
          <li><code>locations.json</code>, <code>teams.json</code> — locations and teams;</li>
          <li><code>users.csv</code> — a limited column list per employee (id, name, email, role, location/team ID and <code>deleted_at</code> if applicable);</li>
          <li><code>shifts.csv</code>, <code>leave.csv</code>, <code>swaps.csv</code> — operational data;</li>
          <li><code>invitations.csv</code> — invitations sent;</li>
          <li><code>audit.csv</code> — audit log entries from the last 12 months;</li>
          <li><code>_manifest.json</code>.</li>
        </ul>
        <p>Here too: no password hashes, refresh tokens or other secrets. Company exports are also limited to three downloads per hour per company.</p>
      </>
    ),
  },
  {
    id: "rechten",
    title: "User rights",
    body: (
      <>
        <p>
          Under privacy law you broadly have the following rights. These rights
          are not absolute: they may be limited by statutory retention
          obligations of the employer, by the rights and freedoms of others
          or by demonstrable legitimate interests.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Information</strong> — you have the right to clear information about how Rostr and your employer handle your data. This statement is part of that.</li>
          <li><strong>Access</strong> — most of your own data is directly visible in the app; the personal export gives you a structured overview.</li>
          <li><strong>Correction</strong> — many personal fields (name, phone, date of birth, preferences) you can adjust yourself in your profile. For data recorded by your employer about you (for example job title or team), you can contact your employer.</li>
          <li><strong>Deletion</strong> — you can delete your account (see section 13). Historical company records may be retained.</li>
          <li><strong>Restriction and objection</strong> — where applicable you can ask to restrict processing or object to certain processing. These requests are assessed case by case.</li>
          <li><strong>Data portability</strong> — where applicable you can request a machine-readable export; for your own data that is the personal export.</li>
          <li><strong>Complaint to a supervisory authority</strong> — you have the right to lodge a complaint with the competent data protection authority. In the Netherlands this is the Autoriteit Persoonsgegevens (<a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="underline">autoriteitpersoonsgegevens.nl</a>).</li>
        </ul>
        <p>
          For personnel data your employer processes via Rostr, that employer
          is often the first point of contact for exercising these rights.
          Rostr supports you and your employer where we can (for example with
          exports and account deletion), but does not step into the
          employer's place on questions that are primarily about the
          employment relationship.
        </p>
      </>
    ),
  },
  {
    id: "geautomatiseerd",
    title: "Automated decision-making",
    body: (
      <>
        <p>
          Rostr does not currently make decisions solely on the basis of
          automated processing or profiling that significantly affects you
          legally or otherwise. Schedules are set and published by managers or
          team leads; leave requests and hours are reviewed by humans;
          individual privacy overrides are configured by the owner.
        </p>
        <p>
          Automated processes that do exist — for example the time-driven
          removal of audit entries after 24 months — do not affect individual
          decisions about people.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies, localStorage and session storage",
    body: (
      <>
        <p>In your browser Rostr uses:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>HTTP-only cookies for authentication</strong> — an <code>access_token</code> and <code>refresh_token</code> cookie (both HttpOnly + Secure) that keep you signed in. These cookies cannot be read from JavaScript.</li>
          <li><strong>A <code>csrf_token</code> cookie</strong> — deliberately readable so the app can echo a matching <code>X-CSRF-Token</code> header on state-changing requests. This token contains no personal data and no session secret.</li>
          <li><strong>localStorage</strong> — used only for preference data the app needs quickly: language (<code>rooster.lang</code>), theme (<code>rostr.theme</code>), "welcome onboarding seen", "what's new seen", the dashboard widget configuration and the dismiss status of the install banner. Your access and refresh tokens are deliberately <em>not</em> stored here.</li>
          <li><strong>sessionStorage</strong> — for the temporary intent and payload during Google sign-in, and for "welcome screen already shown in this session".</li>
        </ul>
        <p>
          At this time there is <strong>no</strong> tracking, marketing or
          advertising cookie in use, and no third-party analytics integration
          in the app. If that changes in the future, this statement is
          updated and we will — where legally required — ask for consent.
        </p>
      </>
    ),
  },
  {
    id: "beveiliging",
    title: "Security",
    body: (
      <>
        <p>Rostr has, amongst others, the following measures in place:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Authentication via password (with bcrypt hashing) or via Emergent-managed Google Sign-In.</li>
          <li>Role-based permissions (Owner, Manager, Team lead, Employee) enforced server-side on all relevant API routes.</li>
          <li>Tenant isolation: a user in company A cannot see or call data of company B.</li>
          <li>The Privacy Matrix and individual privacy overrides for fine-grained visibility per role and per employee.</li>
          <li>Rate limiting on sensitive actions, for example a maximum of three personal exports per hour per user and three company exports per hour per company.</li>
          <li>Password re-authentication on destructive actions such as account or company deletion.</li>
          <li>Audit logging of administrative actions with a technical retention of 24 months.</li>
          <li>Password hashes, refresh tokens and other secrets are never included in exports; the serialisation path explicitly strips these fields.</li>
        </ul>
        <p>
          Rostr claims <strong>no</strong> formal security certifications that
          have not been granted (such as SOC 2 or ISO 27001) and does not use
          terms like "bank-level security" or "100% safe". If that changes in
          the future, this statement is updated.
        </p>
      </>
    ),
  },
  {
    id: "incidenten",
    title: "Security incidents and data breaches",
    body: (
      <>
        <p>
          If Rostr, when processing customer data as a processor, detects a
          security incident, Rostr informs the relevant customer / controller
          under the applicable arrangements without undue delay. That
          customer then assesses any notification obligations towards the
          supervisory authority and data subjects; Rostr assists where we can.
        </p>
        <p>
          For incidents involving data for which the organisation behind
          Rostr is itself responsible (for example account security or
          billing data), Rostr assesses any notification obligation itself.
        </p>
        <p>
          Rostr expressly does <strong>not</strong> claim a 24/7 security
          operations centre or fixed response times. The full, formal
          incident and data-breach procedure — including internal
          responsibilities, escalation and communication templates — will be
          finalised together with the legal review before commercial launch.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact and changes",
    body: (
      <>
        <p>
          Questions about this privacy statement, your data, an export or
          account deletion can be sent to{" "}
          <a
            href="mailto:rosterhelp@gmail.com"
            className="underline hover:text-[hsl(var(--primary))]"
          >
            rosterhelp@gmail.com
          </a>. For general information about Rostr, you can reach us at{" "}
          <a
            href="mailto:info.roster@gmail.com"
            className="underline hover:text-[hsl(var(--primary))]"
          >
            info.roster@gmail.com
          </a>. Other addresses — for example at @rostr.eu or a specific data
          protection officer — will only be communicated once they have
          actually been created and confirmed.
        </p>
        <p>
          Rostr may update this privacy statement when functionality changes
          or when required by law or regulation. The date at the top of the
          page indicates when the text was last updated.
        </p>
      </>
    ),
  },
];

/* ================================================================== */
/*  TERMS — Nederlands (Fase 6.2 — inhoudelijk niet gewijzigd in 6.3) */
/* ================================================================== */
const TERMS_NL = [
  {
    id: "toepasselijkheid",
    title: "Toepasselijkheid",
    body: (
      <p>
        Deze voorwaarden zijn van toepassing op het gebruik van Rostr, het
        SaaS-platform voor personeelsplanning. Door een account aan te maken
        of te gebruiken, aanvaard je deze voorwaarden.
      </p>
    ),
  },
  {
    id: "accountregistratie",
    title: "Accountregistratie",
    body: (
      <p>
        Om Rostr te gebruiken maak je een account aan met een geldig e-mailadres
        of via Google-inloggen. Je geeft juiste gegevens op en houdt ze
        actueel.
      </p>
    ),
  },
  {
    id: "verantwoordelijkheid-account",
    title: "Verantwoordelijkheid voor het account",
    body: (
      <p>
        Je bent verantwoordelijk voor het gebruik van je account en voor het
        geheimhouden van je inloggegevens. Vermoed je misbruik, meld dat dan
        via <a className="underline" href="mailto:rosterhelp@gmail.com">rosterhelp@gmail.com</a>.
      </p>
    ),
  },
  {
    id: "rollen",
    title: "Rollen binnen een bedrijf",
    body: (
      <p>
        Binnen een bedrijf zijn er vier rollen: Eigenaar, Manager, Teamleider
        en Medewerker. De eigenaar bepaalt hoe rechten worden verdeeld en welke
        instellingen gelden voor het bedrijf.
      </p>
    ),
  },
  {
    id: "toegestaan-gebruik",
    title: "Toegestaan gebruik",
    body: (
      <p>
        Rostr mag worden gebruikt voor de eigen personeelsplanning van het
        bedrijf dat een account heeft. Elke andere commerciële herexploitatie
        (bijvoorbeeld doorverkopen als eigen dienst) is niet toegestaan zonder
        schriftelijke toestemming.
      </p>
    ),
  },
  {
    id: "verboden",
    title: "Verboden gebruik en misbruik",
    body: (
      <>
        <p>Het is onder meer niet toegestaan om:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Toegang te proberen te krijgen tot gegevens van andere bedrijven of
            gebruikers dan die waar je zelf toe gerechtigd bent.
          </li>
          <li>De dienst te verstoren of overmatig te belasten.</li>
          <li>Reverse engineering, scraping of geautomatiseerd misbruik toe te passen.</li>
          <li>De dienst te gebruiken op een manier die strijdig is met de wet.</li>
        </ul>
      </>
    ),
  },
  {
    id: "personeelsgegevens",
    title: "Personeelsgegevens en verantwoordelijkheid van het bedrijf",
    body: (
      <p>
        Een bedrijf dat Rostr gebruikt is zelf verantwoordelijk voor de
        gegevens die het van zijn werknemers in Rostr invoert en voor de
        gemaakte afspraken met die werknemers. In termen van de AVG treedt
        het bedrijf op als verwerkingsverantwoordelijke voor die
        personeelsgegevens; Rostr treedt op als verwerker. De praktische
        invulling en de exacte tekst van de bijbehorende
        verwerkersovereenkomst worden vóór de commerciële lancering apart
        vastgelegd.
      </p>
    ),
  },
  {
    id: "abonnement",
    title: "Abonnement en betalingen",
    body: (
      <>
        <p>
          De eigenaar van een bedrijf kan een abonnement afsluiten voor het
          gebruik van Rostr. Facturatie en betalingen lopen via de
          betaalprovider die op dat moment binnen Rostr is geïntegreerd.
        </p>
        <p>
          Prijzen en pakketten worden getoond binnen de app. Deze kunnen
          wijzigen; wijzigingen gaan pas in nadat je bent geïnformeerd of bij
          een nieuwe factuurperiode. De concrete voorwaarden rondom een
          eventuele proefperiode en het refund-beleid worden vóór de
          commerciële lancering apart vastgelegd in de op dat moment
          geldende versie van dit document.
        </p>
      </>
    ),
  },
  {
    id: "beschikbaarheid",
    title: "Beschikbaarheid en onderhoud",
    body: (
      <p>
        Rostr streeft naar een hoge beschikbaarheid maar geeft geen
        ononderbroken garantie. Gepland onderhoud wordt waar mogelijk
        aangekondigd; ongepland onderhoud kan voorkomen wanneer dat nodig is
        voor beveiliging of stabiliteit.
      </p>
    ),
  },
  {
    id: "wijzigingen-dienst",
    title: "Wijzigingen aan de dienst",
    body: (
      <p>
        Rostr mag de functionaliteit van de dienst aanpassen, uitbreiden of
        onderdelen buiten gebruik stellen. Wijzigingen die grote impact hebben
        op bestaande workflows worden aangekondigd in de app.
      </p>
    ),
  },
  {
    id: "beeindiging-account",
    title: "Beëindiging: account",
    body: (
      <p>
        Je kunt je eigen account op elk moment verwijderen vanuit je profiel.
        Zie het privacybeleid voor wat er dan met je gegevens gebeurt.
      </p>
    ),
  },
  {
    id: "beeindiging-bedrijf",
    title: "Beëindiging: bedrijf",
    body: (
      <p>
        De eigenaar van een bedrijf kan het bedrijf verwijderen. Er geldt een
        grace period van 30 dagen, waarna het bedrijf in aanmerking komt voor
        definitieve verwijdering volgens de procedure die is beschreven in het
        privacybeleid.
      </p>
    ),
  },
  {
    id: "intellectueel-eigendom",
    title: "Intellectueel eigendom",
    body: (
      <p>
        Rostr, de software, het merk en de vormgeving zijn en blijven eigendom
        van de rechthebbenden. Gebruik van Rostr geeft geen overdracht van
        rechten aan de gebruiker of het bedrijf.
      </p>
    ),
  },
  {
    id: "aansprakelijkheid",
    title: "Aansprakelijkheid",
    body: (
      <p>
        Rostr wordt geleverd op basis van de huidige stand van het product.
        Dit conceptbeleid geeft nog geen definitieve juridische afbakening
        van aansprakelijkheid. De concrete aansprakelijkheids- en
        garantiebepalingen worden vóór de commerciële lancering apart
        vastgelegd in de op dat moment geldende versie van dit document.
      </p>
    ),
  },
  {
    id: "privacyverwijzing",
    title: "Privacyverwijzing",
    body: (
      <p>
        Op de verwerking van persoonsgegevens binnen Rostr is het{" "}
        <Link to="/privacy" className="underline">privacybeleid</Link> van
        toepassing. Dat maakt onlosmakelijk onderdeel uit van deze voorwaarden.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Vragen over deze voorwaarden? Neem contact op via{" "}
        <a className="underline" href="mailto:rosterhelp@gmail.com">
          rosterhelp@gmail.com
        </a>
        .
      </p>
    ),
  },
  {
    id: "versie",
    title: "Versie",
    body: (
      <p>
        Deze voorwaarden zijn een conceptversie. De datum bovenaan geeft aan
        wanneer de tekst voor het laatst is bijgewerkt.
      </p>
    ),
  },
];

/* ================================================================== */
/*  TERMS — English (Fase 6.2 — inhoudelijk niet gewijzigd in 6.3)    */
/* ================================================================== */
const TERMS_EN = [
  {
    id: "toepasselijkheid",
    title: "Scope",
    body: (
      <p>
        These terms apply to the use of Rostr, the SaaS platform for
        workforce scheduling. By creating or using an account you accept these
        terms.
      </p>
    ),
  },
  {
    id: "accountregistratie",
    title: "Account registration",
    body: (
      <p>
        To use Rostr you create an account with a valid email address or via
        Google sign-in. You provide accurate information and keep it up to
        date.
      </p>
    ),
  },
  {
    id: "verantwoordelijkheid-account",
    title: "Account responsibility",
    body: (
      <p>
        You are responsible for the use of your account and for keeping your
        credentials confidential. If you suspect abuse, report it via{" "}
        <a className="underline" href="mailto:rosterhelp@gmail.com">
          rosterhelp@gmail.com
        </a>
        .
      </p>
    ),
  },
  {
    id: "rollen",
    title: "Roles within a company",
    body: (
      <p>
        A company has four roles: Owner, Manager, Team lead and Employee. The
        owner decides how permissions are distributed and which settings apply
        to the company.
      </p>
    ),
  },
  {
    id: "toegestaan-gebruik",
    title: "Permitted use",
    body: (
      <p>
        Rostr may be used for the workforce planning of the company that owns
        the account. Any other commercial re-exploitation (for example
        reselling as a separate service) is not allowed without written
        permission.
      </p>
    ),
  },
  {
    id: "verboden",
    title: "Prohibited use and abuse",
    body: (
      <>
        <p>Among other things, the following is not allowed:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Attempting to access data of other companies or users beyond your
            own authorisation.
          </li>
          <li>Disrupting the service or generating excessive load.</li>
          <li>Reverse engineering, scraping or automated abuse.</li>
          <li>Using the service in a way that violates the law.</li>
        </ul>
      </>
    ),
  },
  {
    id: "personeelsgegevens",
    title: "Employee data and company responsibility",
    body: (
      <p>
        A company using Rostr is itself responsible for the data it enters
        about its employees into Rostr and for the agreements it has with
        those employees. Under the GDPR the company acts as data controller
        for that employee data; Rostr acts as data processor. The practical
        implementation and the exact wording of the accompanying data
        processing agreement will be finalised separately before commercial
        launch.
      </p>
    ),
  },
  {
    id: "abonnement",
    title: "Subscription and payments",
    body: (
      <>
        <p>
          The owner of a company can take out a subscription for the use of
          Rostr. Billing and payments run through the payment provider
          currently integrated with Rostr.
        </p>
        <p>
          Prices and plans are shown inside the app. These may change; changes
          take effect after notification or at a new billing period. The
          concrete terms for any trial period and the refund policy will be
          finalised separately before commercial launch in the then-current
          version of this document.
        </p>
      </>
    ),
  },
  {
    id: "beschikbaarheid",
    title: "Availability and maintenance",
    body: (
      <p>
        Rostr aims for high availability but does not guarantee uninterrupted
        service. Planned maintenance is announced where possible; unplanned
        maintenance can occur when needed for security or stability.
      </p>
    ),
  },
  {
    id: "wijzigingen-dienst",
    title: "Changes to the service",
    body: (
      <p>
        Rostr may adjust or extend the functionality of the service, or retire
        parts of it. Changes with significant impact on existing workflows are
        announced inside the app.
      </p>
    ),
  },
  {
    id: "beeindiging-account",
    title: "Termination: account",
    body: (
      <p>
        You can delete your own account at any time from your profile. See the
        privacy policy for what happens to your data.
      </p>
    ),
  },
  {
    id: "beeindiging-bedrijf",
    title: "Termination: company",
    body: (
      <p>
        The owner of a company can delete the company. A 30-day grace period
        applies, after which the company becomes eligible for final removal
        via the procedure described in the privacy policy.
      </p>
    ),
  },
  {
    id: "intellectueel-eigendom",
    title: "Intellectual property",
    body: (
      <p>
        Rostr, its software, brand and design remain the property of the
        rights holders. Using Rostr does not transfer any rights to the user
        or the company.
      </p>
    ),
  },
  {
    id: "aansprakelijkheid",
    title: "Liability",
    body: (
      <p>
        Rostr is provided based on the current state of the product. This
        draft policy does not yet establish a definitive legal boundary of
        liability. Concrete liability and warranty provisions will be
        finalised separately before commercial launch in the then-current
        version of this document.
      </p>
    ),
  },
  {
    id: "privacyverwijzing",
    title: "Privacy reference",
    body: (
      <p>
        The processing of personal data within Rostr is governed by the{" "}
        <Link to="/privacy" className="underline">privacy policy</Link>, which
        forms an integral part of these terms.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Questions about these terms? Contact us via{" "}
        <a className="underline" href="mailto:rosterhelp@gmail.com">
          rosterhelp@gmail.com
        </a>
        .
      </p>
    ),
  },
  {
    id: "versie",
    title: "Version",
    body: (
      <p>
        These terms are a draft version. The date at the top indicates when
        the text was last updated.
      </p>
    ),
  },
];

/* ------------------------------------------------------------------ */
/*  Exports                                                           */
/* ------------------------------------------------------------------ */
export function PrivacyPage() {
  const { t, lang } = useI18n();
  const sections = lang === "nl" ? PRIVACY_NL : PRIVACY_EN;
  return <Shell title={t("legal_privacy")} docKey="privacy" sections={sections} />;
}

export function TermsPage() {
  const { t, lang } = useI18n();
  const sections = lang === "nl" ? TERMS_NL : TERMS_EN;
  return <Shell title={t("legal_terms")} docKey="terms" sections={sections} />;
}
